import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dismiss12Regular } from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { toast } from 'react-toastify';
import { ObservationFormSidebar } from '../../Observations';
import { PostInformationRequestSidebar } from '../../IRL';
import AskAiQuickCreateSidebar from '../AskAiQuickCreateSidebar';
import { saveInsightsModuleToJournal, buildInsightsItemJournalBody } from '../../../utils/askLensJournal';
import {
    filterSupportedInsightsActions,
    isInsightsReadOnlyBlocked,
} from '../../../utils/intelligenceInsightsActions';
import InsightsModuleChrome from './InsightsModuleChrome';
import { useInsightsReadOnly } from './InsightsReadOnlyContext';
import HighlightsEvidencePanel from './HighlightsEvidencePanel';
import ModuleEmptyState from './ModuleEmptyState';
import styles from './intelligenceInsightsModules.module.css';
import drawerStyles from './intelligenceInsightsHighlights.module.css';

const RAG_CLASS = {
    amber: styles.ragAmber,
    green: styles.ragGreen,
    red: styles.ragRed,
    grey: styles.ragGrey,
};

const TABS = [
    { key: 'positives', label: 'Positives' },
    { key: 'concerns', label: 'Concerns' },
    { key: 'unresolved', label: 'Unresolved' },
];

function itemPrefill(item) {
    return [item?.title, item?.body, item?.whyItMatters].filter(Boolean).join('\n\n');
}

function DetailDrawer({ open, item, areaLabel, onClose, onAction }) {
    if (!open || !item) return null;
    const detail = item.detail || {};
    const fullAssessment = detail.fullAssessment || item.body || '';
    const whyItMatters = item.whyItMatters || '';
    const recommended = detail.recommendedResponse || '';
    const evidenceCount = (item.evidenceReferenceIds || []).length;
    const viewEvidenceAction =
        (item.actions || []).find(
            a => a.key === 'view_evidence' || a.key === 'open_evidence_panel',
        ) || { key: 'view_evidence', label: 'View evidence' };
    const journalAction =
        (item.actions || []).find(a => a.key === 'save_to_journal') || {
            key: 'save_to_journal',
            label: 'Save to Journal',
        };
    const otherActions = filterSupportedInsightsActions(
        (item.actions || []).filter(
            a =>
                a.key &&
                ![
                    'view_details',
                    'view_evidence',
                    'open_evidence_panel',
                    'save_to_journal',
                    'open_risks_signals',
                    'open_key_findings',
                    'open_evidence_follow_ups',
                ].includes(a.key),
        ),
        { moduleKey: 'key_findings', context: { item } },
    );
    // Ensure common KF actions are always available even if API omitted them.
    const fallbackActions = [
        { key: 'add_to_raid', label: 'Add to RAID' },
        { key: 'create_observation', label: 'Create Observation' },
    ].filter(
        fallback =>
            !otherActions.some(a => a.key === fallback.key) &&
            filterSupportedInsightsActions([fallback], {
                moduleKey: 'key_findings',
                context: { item },
            }).length,
    );
    const ghostActions = [...otherActions, ...fallbackActions];

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={typeof document !== 'undefined' ? document.body : undefined}
            onOpenChange={(_, data) => {
                if (!data.open) onClose?.();
            }}
            style={{ width: 480, maxWidth: '100vw', background: '#fff' }}>
            <DrawerBody className={drawerStyles.detailDrawerBody} style={{ width: 480, background: '#fff' }}>
                <div className={styles.swotDetailRoot}>
                    <div className={styles.swotDetailHeader}>
                        <div className={styles.swotDetailHeaderText}>
                            <p className={styles.swotDetailEyebrow}>
                                {areaLabel || 'Key Findings'}
                            </p>
                            <h2 className={styles.swotDetailTitle}>{item.title}</h2>
                        </div>
                        <button
                            type="button"
                            className={drawerStyles.drawerClose}
                            onClick={onClose}
                            aria-label="Close">
                            <Dismiss12Regular />
                        </button>
                    </div>

                    <div className={styles.swotDetailBody}>
                        {fullAssessment ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Full Assessment</p>
                                <p className={styles.swotDetailLead}>{fullAssessment}</p>
                            </section>
                        ) : null}

                        {whyItMatters ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Why It Matters</p>
                                <p className={styles.swotDetailText}>{whyItMatters}</p>
                            </section>
                        ) : null}

                        {recommended ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Recommended Response</p>
                                <div className={styles.swotDetailResponse}>{recommended}</div>
                            </section>
                        ) : null}

                        {item.relatedRiskIds?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Related Risks</p>
                                <button
                                    type="button"
                                    className={styles.swotDetailEvidenceLink}
                                    onClick={() =>
                                        onAction?.(
                                            {
                                                key: 'open_risks_signals',
                                                label: 'Related Risks →',
                                            },
                                            item,
                                        )
                                    }>
                                    Open Risks & Signals →
                                </button>
                            </section>
                        ) : null}

                        <section className={styles.swotDetailSection}>
                            <p className={styles.swotDetailLabel}>Evidence</p>
                            <div className={styles.swotDetailEvidenceRow}>
                                <span className={styles.swotDetailEvidenceCount}>
                                    {evidenceCount > 0
                                        ? `${evidenceCount} evidence item${
                                              evidenceCount === 1 ? '' : 's'
                                          }`
                                        : 'Evidence available'}
                                </span>
                                <button
                                    type="button"
                                    className={styles.swotDetailEvidenceLink}
                                    onClick={() => onAction?.(viewEvidenceAction, item)}>
                                    View evidence
                                </button>
                            </div>
                        </section>

                        <div className={styles.swotDetailActions}>
                            <button
                                type="button"
                                className={styles.swotDetailGhostBtn}
                                onClick={() => onAction?.(journalAction, item)}>
                                Save to Journal
                            </button>
                            {ghostActions.map(action => (
                                <button
                                    key={action.key}
                                    type="button"
                                    className={styles.swotDetailGhostBtn}
                                    onClick={() => onAction?.(action, item)}>
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

const IntelligenceInsightsKeyFindings = ({
    projectId,
    data,
    loading,
    error,
    onAskAi,
    onExport,
    exportEnabled = false,
    onOpenModule,
    onGenerateAnalysis,
    onGoToOverview,
    onNewEvidenceAction,
    onRetry,
    generating = false,
    initialScopeArea = null,
    historicalVersion = null,
    onExitHistoricalView = null,
}) => {
    const dispatch = useDispatch();
    const notesState = useSelector(state => state?.dashboard?.notes);
    const readOnly = useInsightsReadOnly();

    const areas = data?.areas || [];
    const [selectedKey, setSelectedKey] = useState(
        initialScopeArea || data?.activeScopeKey || areas[0]?.key || null,
    );
    const [tab, setTab] = useState('positives');
    const [rationaleOpen, setRationaleOpen] = useState(true);
    const [materialityFilter, setMaterialityFilter] = useState('all');
    const [confidenceFilter, setConfidenceFilter] = useState('all');
    const [unresolvedOnly, setUnresolvedOnly] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [evidenceTarget, setEvidenceTarget] = useState(null);
    const [sidebarContent, setSidebarContent] = useState('');
    const [showIrlSidebar, setShowIrlSidebar] = useState(false);
    const [showRaidSidebar, setShowRaidSidebar] = useState(false);
    const [showObsSidebar, setShowObsSidebar] = useState(false);

    useEffect(() => {
        if (!areas.length) return;
        if (initialScopeArea && areas.find(a => a.key === initialScopeArea)) {
            setSelectedKey(initialScopeArea);
            return;
        }
        if (!selectedKey || !areas.find(a => a.key === selectedKey)) {
            setSelectedKey(data?.activeScopeKey || areas[0].key);
        }
    }, [areas, selectedKey, initialScopeArea, data?.activeScopeKey]);

    const area = useMemo(
        () => areas.find(a => a.key === selectedKey) || areas[0],
        [areas, selectedKey],
    );

    const handleAskLens = useCallback(() => {
        onAskAi?.({
            scopeArea: area?.key || selectedKey || null,
            scopeAreaLabel: area?.label || null,
        });
    }, [onAskAi, area, selectedKey]);

    const filterItem = (item, tabKey) => {
        if (unresolvedOnly && tabKey !== 'unresolved') return false;
        if (materialityFilter !== 'all') {
            if (
                !String(item.materiality || '')
                    .toLowerCase()
                    .includes(materialityFilter.toLowerCase())
            ) {
                return false;
            }
        }
        if (confidenceFilter !== 'all') {
            if (
                !item.confidence ||
                String(item.confidence).toLowerCase() !== confidenceFilter.toLowerCase()
            ) {
                return false;
            }
        }
        return true;
    };

    const filteredItems = useMemo(() => {
        if (!area) return [];
        return (area.items?.[tab] || []).filter(item => filterItem(item, tab));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- filterItem closes over filter state
    }, [area, tab, materialityFilter, confidenceFilter, unresolvedOnly]);

    const counts = useMemo(() => {
        if (!area) {
            return { positives: 0, concerns: 0, unresolved: 0 };
        }
        return {
            positives: (area.items?.positives || []).filter(item => filterItem(item, 'positives'))
                .length,
            concerns: (area.items?.concerns || []).filter(item => filterItem(item, 'concerns'))
                .length,
            unresolved: (area.items?.unresolved || []).filter(item =>
                filterItem(item, 'unresolved'),
            ).length,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area, materialityFilter, confidenceFilter, unresolvedOnly]);

    const handleAction = async (action, item = null) => {
        if (!action || isInsightsReadOnlyBlocked(readOnly, action)) return;
        const key = action.key || action.actionType || action.action_type;
        const target = action.target || {};
        const text = itemPrefill(item) || [area?.summary, area?.rationale].filter(Boolean).join('\n\n');

        if (key === 'view_details') {
            setEvidenceTarget(null);
            setDetailItem(item);
            return;
        }
        if (key === 'view_evidence' || key === 'open_evidence_panel') {
            setDetailItem(null);
            const objectType = target.object_type || target.type;
            const objectId = target.object_id || target.id;
            if (
                objectType &&
                objectId &&
                ['finding', 'evidence_matter', 'risk', 'theme', 'contradiction'].includes(objectType)
            ) {
                setEvidenceTarget({
                    mode: 'object',
                    objectType,
                    objectId,
                    area: area?.key,
                });
                return;
            }
            setEvidenceTarget({ mode: 'scope', area: area?.key });
            return;
        }
        if (key === 'create_irl') {
            setSidebarContent(text);
            setShowIrlSidebar(true);
            return;
        }
        if (key === 'add_to_raid' || key === 'add_raid') {
            setSidebarContent(text);
            setShowRaidSidebar(true);
            return;
        }
        if (key === 'create_observation') {
            setSidebarContent(text);
            setShowObsSidebar(true);
            return;
        }
        if (key === 'save_to_journal') {
            try {
                await saveInsightsModuleToJournal({
                    dispatch,
                    notesState,
                    moduleLabel: 'Key Findings',
                    headline: item?.title || area?.label || '',
                    bodyText: buildInsightsItemJournalBody(item) || text,
                });
                toast.success('Saved to Journal');
            } catch {
                toast.error('Could not save to Journal');
            }
            return;
        }
        if (key === 'open_risks_signals') {
            onOpenModule?.('risks_signals');
            return;
        }
        if (key === 'open_evidence_follow_ups') {
            onOpenModule?.('evidence_follow_ups');
            return;
        }
    };

    if (error || !data || data.isEmpty) {
        const failed =
            Boolean(error) ||
            data?.status === 'failed' ||
            data?.emptyState?.type === 'failed';
        return (
            <InsightsModuleChrome
                title={data?.title || 'Key Findings'}
                subtitle={
                    data?.subtitle ||
                    'Detailed technology assessment across eight controlled areas.'
                }
                metaLine={data?.metaLine || 'Entire Project'}
                onAskAi={handleAskLens}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                <ModuleEmptyState
                    moduleKey="key_findings"
                    loading={Boolean(loading && !error)}
                    error={error}
                    emptyState={data?.emptyState}
                    status={data?.status}
                    variant={failed ? 'failed' : 'not_generated'}
                    generating={generating}
                    onGoToOverview={onGoToOverview}
                    onGenerateAnalysis={onGenerateAnalysis}
                    onRetry={onRetry}
                />
            </InsightsModuleChrome>
        );
    }

    if (!area) {
        return null;
    }

    return (
        <>
            <InsightsModuleChrome
                title={data.title}
                subtitle={data.subtitle}
                metaLine={data.metaLine}
                onAskAi={handleAskLens}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}
                demoBadge={false}>
                {data.warnings?.length ? (
                    <div className={styles.footerBanner} style={{ marginBottom: 16 }}>
                        <div>
                            <p className={styles.footerBannerTitle}>Complete with warnings</p>
                            <p className={styles.footerBannerMeta}>
                                {data.warnings[0]?.message ||
                                    'Some sources were unavailable during assessment.'}
                            </p>
                        </div>
                    </div>
                ) : null}

                <div className={styles.kfLayout}>
                    <aside className={styles.kfList}>
                        {areas.map(item => (
                            <button
                                key={item.key}
                                type="button"
                                className={`${styles.kfListItem} ${
                                    item.key === area.key ? styles.kfListItemActive : ''
                                }`}
                                onClick={() => {
                                    setSelectedKey(item.key);
                                    setTab('positives');
                                    setDetailItem(null);
                                }}>
                                <div className={styles.scopeTitleRow}>
                                    <span
                                        className={`${styles.ragPill} ${
                                            RAG_CLASS[item.rag] || styles.ragGrey
                                        }`}>
                                        <span className={styles.ragDot} aria-hidden />
                                        {item.label}
                                    </span>
                                </div>
                                <div className={styles.kfCounts}>
                                    <span>{item.positives} Positives</span>
                                    <span>{item.concerns} Concerns</span>
                                    <span>{item.unresolved} Unresolved</span>
                                </div>
                            </button>
                        ))}
                    </aside>

                    <div className={styles.kfDetail}>
                        <section className={styles.kfSummaryCard}>
                            <div className={styles.chipRow} style={{ marginBottom: 8 }}>
                                <span
                                    className={`${styles.ragPill} ${
                                        RAG_CLASS[area.rag] || styles.ragGrey
                                    }`}>
                                    <span className={styles.ragDot} aria-hidden />
                                    {area.rag}
                                </span>
                                <strong style={{ color: '#1b2149' }}>{area.label}</strong>
                                <span style={{ color: '#6b7280', fontSize: 12 }}>
                                    {area.positives} positives · {area.concerns} concerns ·{' '}
                                    {area.unresolved} unresolved
                                </span>
                            </div>
                            {typeof area.summary === 'string' && area.summary ? (
                                <p className={styles.sectionText}>{area.summary}</p>
                            ) : null}
                            <div style={{ marginTop: 10 }}>
                                <button
                                    type="button"
                                    className={styles.btnLink}
                                    onClick={() => setRationaleOpen(v => !v)}>
                                    {rationaleOpen ? 'Hide rationale' : 'Show rationale'}
                                </button>
                                {rationaleOpen ? (
                                    <p className={styles.confidence} style={{ marginTop: 6 }}>
                                        {area.rationale}
                                    </p>
                                ) : null}
                            </div>
                            <div className={styles.actionRow} style={{ marginTop: 12 }}>
                                <button
                                    type="button"
                                    className={styles.btnLink}
                                    disabled={readOnly}
                                    onClick={() =>
                                        handleAction({ key: 'add_to_raid', label: 'Add to RAID' })
                                    }>
                                    Add to RAID
                                </button>
                                <button
                                    type="button"
                                    className={styles.btnLink}
                                    onClick={() =>
                                        handleAction({
                                            key: 'view_evidence',
                                            label: 'View evidence',
                                            target: { type: 'scope_area', id: area.key },
                                        })
                                    }>
                                    View evidence
                                </button>
                            </div>
                        </section>

                        <section className={styles.section}>
                            <div className={styles.tabs}>
                                {TABS.map(t => (
                                    <button
                                        key={t.key}
                                        type="button"
                                        className={`${styles.tab} ${
                                            tab === t.key ? styles.tabActive : ''
                                        }`}
                                        onClick={() => setTab(t.key)}>
                                        {t.label} ({counts[t.key]})
                                    </button>
                                ))}
                            </div>

                            <div className={styles.filters} style={{ marginBottom: 12 }}>
                                <div className={styles.filterGroup}>
                                    <span className={styles.filterLabel}>Materiality:</span>
                                    <select
                                        className={styles.filterSelect}
                                        value={materialityFilter}
                                        onChange={e => setMaterialityFilter(e.target.value)}
                                        aria-label="Materiality filter">
                                        {(
                                            data.filters?.options?.materiality || [
                                                'all',
                                                'material',
                                                'notable',
                                            ]
                                        ).map(opt => (
                                            <option key={opt} value={opt}>
                                                {opt === 'all' ? 'All' : titleCase(opt)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.filterGroup}>
                                    <span className={styles.filterLabel}>Confidence:</span>
                                    <select
                                        className={styles.filterSelect}
                                        value={confidenceFilter}
                                        onChange={e => setConfidenceFilter(e.target.value)}
                                        aria-label="Confidence filter">
                                        {(
                                            data.filters?.options?.confidence || [
                                                'all',
                                                'high',
                                                'medium',
                                                'low',
                                            ]
                                        ).map(opt => (
                                            <option key={opt} value={opt}>
                                                {opt === 'all' ? 'All' : titleCase(opt)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <label className={styles.filterCheckbox}>
                                    <input
                                        type="checkbox"
                                        checked={unresolvedOnly}
                                        onChange={e => {
                                            setUnresolvedOnly(e.target.checked);
                                            if (e.target.checked) setTab('unresolved');
                                        }}
                                    />
                                    Unresolved only
                                </label>
                            </div>

                            {filteredItems.length ? (
                                filteredItems.map(finding => {
                                    const isUnresolved =
                                        finding.type === 'evidence_matter' ||
                                        finding.type === 'contradiction';
                                    const cardClass = [
                                        styles.findingCard,
                                        finding.type === 'evidence_matter'
                                            ? styles.findingCardGap
                                            : '',
                                        finding.type === 'contradiction'
                                            ? styles.findingCardContradiction
                                            : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ');
                                    const defaultActions =
                                        tab === 'unresolved'
                                            ? [
                                                  { key: 'create_irl', label: 'Create IRL' },
                                                  {
                                                      key: 'view_evidence',
                                                      label: 'View evidence',
                                                  },
                                              ]
                                            : [
                                                  { key: 'view_details', label: 'View details' },
                                                  {
                                                      key: 'view_evidence',
                                                      label: 'View evidence',
                                                  },
                                              ];
                                    const actions = filterSupportedInsightsActions(
                                        finding.actions?.length ? finding.actions : defaultActions,
                                        { moduleKey: 'key_findings', context: { item: finding } },
                                    );

                                    return (
                                        <article key={finding.id} className={cardClass}>
                                            <div className={styles.chipRow} style={{ marginBottom: 6 }}>
                                                {isUnresolved ? (
                                                    <>
                                                        {finding.typeLabel ? (
                                                            <span
                                                                className={`${styles.chip} ${
                                                                    finding.type === 'contradiction'
                                                                        ? styles.chipContradiction
                                                                        : styles.chipEvidenceGap
                                                                }`}>
                                                                {finding.typeLabel}
                                                            </span>
                                                        ) : null}
                                                        {finding.priorityLabel ? (
                                                            <span
                                                                className={`${styles.chip} ${styles.chipPriority}`}>
                                                                {finding.priorityLabel}
                                                            </span>
                                                        ) : null}
                                                        <span
                                                            className={`${styles.chip} ${styles.chipMateriality}`}>
                                                            {finding.materiality || 'Material'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span
                                                        className={`${styles.chip} ${styles.materialChip}`}>
                                                        {finding.materiality}
                                                    </span>
                                                )}
                                            </div>
                                            <p className={styles.findingTitle}>{finding.title}</p>
                                            <p className={styles.takeawayBody}>{finding.body}</p>
                                            {finding.impactNote ? (
                                                <p className={styles.impactNote}>
                                                    {finding.impactNote}
                                                </p>
                                            ) : null}
                                            {actions.length ? (
                                                <div className={styles.findingActions}>
                                                    {actions.map(action => (
                                                    <button
                                                        key={`${finding.id}-${action.key}`}
                                                        type="button"
                                                        className={
                                                            action.key === 'create_irl'
                                                                ? styles.btnGhost
                                                                : styles.btnLink
                                                        }
                                                        disabled={isInsightsReadOnlyBlocked(
                                                            readOnly,
                                                            action,
                                                        )}
                                                        onClick={() =>
                                                            handleAction(action, finding)
                                                        }>
                                                        {action.label}
                                                    </button>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </article>
                                    );
                                })
                            ) : (
                                <p className={styles.emptyNote}>
                                    No {tab} findings match the current filters for {area.label}.
                                </p>
                            )}
                        </section>
                    </div>
                </div>
            </InsightsModuleChrome>

            <DetailDrawer
                open={Boolean(detailItem)}
                item={detailItem}
                areaLabel={area?.label}
                onClose={() => setDetailItem(null)}
                onAction={handleAction}
            />

            <HighlightsEvidencePanel
                open={Boolean(evidenceTarget)}
                projectId={projectId}
                area={evidenceTarget?.area || area?.key}
                objectType={evidenceTarget?.objectType || null}
                objectId={evidenceTarget?.objectId || null}
                endpointMode={evidenceTarget?.mode === 'object' ? 'object' : 'scope'}
                onClose={() => setEvidenceTarget(null)}
                onOpenModule={onOpenModule}
                onAction={(action, panelCard) => {
                    void handleAction(action, {
                        title: panelCard?.headline || area?.label,
                        body: panelCard?.conclusion || area?.summary,
                    });
                }}
            />

            <PostInformationRequestSidebar
                show={showIrlSidebar}
                setShow={show => {
                    setShowIrlSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultRequest={() => sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showRaidSidebar}
                setShow={show => {
                    setShowRaidSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="raid"
                defaultText={() => sidebarContent || ''}
            />
            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultObservation={sidebarContent || ''}
            />
        </>
    );
};

function titleCase(value) {
    return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

export default IntelligenceInsightsKeyFindings;
